/**
 * Chile — ciudades / comunas de alto poder adquisitivo (DualCTA + promo select).
 *
 * Cloudflare `cf.city` suele devolver el nombre en español (a veces sin tildes).
 * Normalizamos a ASCII lower para matchear variantes.
 *
 * Keep in sync with worker.js `isChileHighPurchasingPowerCity`.
 */

import {
  CANONICAL_VIP_PRICE_USD,
  CANONICAL_VIP_STRIPE_PRICE_ID,
  CANONICAL_VIP_STRIPE_PRODUCT_ID,
  SELECT_VIP_FIRST_MONTH_DISCOUNT_PERCENT,
  SELECT_VIP_FIRST_MONTH_PROMO_ID,
  SELECT_VIP_FIRST_MONTH_PROMO_ID_LEGACY,
  SELECT_VIP_PROMOTION_CODE_NAME,
} from "@/lib/canonicalVipMembership";

/** Promo VIP engagement 80% 1.er mes (DELFU80INTL / SELECT_VIP) — mismo MX + CL high. */
export const CHILE_HIGH_PURCHASING_VIP_PROMO_ID = SELECT_VIP_FIRST_MONTH_PROMO_ID;

/** Live + historical engagement promos (never use LEGACY as exterior *default*). */
export function isEngagementVipPromoId(promoId?: string | null): boolean {
  const promo = String(promoId || "").trim();
  if (!promo) return false;
  return (
    promo === SELECT_VIP_FIRST_MONTH_PROMO_ID ||
    promo === SELECT_VIP_FIRST_MONTH_PROMO_ID_LEGACY ||
    promo.startsWith("promo_1U0KCW")
  );
}

/**
 * Nombres canónicos (display) + aliases que CF puede devolver.
 * Incluye comunas RM oriente / premium y plazas de alto gasto fuera de Santiago.
 */
const CHILE_HIGH_PURCHASING_CITY_NAMES = [
  // RM — oriente y premium
  "Santiago",
  "Las Condes",
  "Vitacura",
  "Lo Barnechea",
  "Providencia",
  "Ñuñoa",
  "Nunoa",
  "La Reina",
  "Huechuraba",
  "Colina", // Chicureo
  "Peñalolén",
  "Penalolen",
  "Macul",
  // Costa y sur premium
  "Viña del Mar",
  "Vina del Mar",
  "Concón",
  "Concon",
  "Reñaca",
  "Renaca",
  "Zapallar",
  "Puerto Varas",
  "Pucón",
  "Pucon",
  // Norte (minería / alto ingreso)
  "Antofagasta",
  "Iquique",
] as const;

export function normalizeChileCityKey(raw?: string | null): string {
  return String(raw || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const CHILE_HIGH_PURCHASING_CITY_KEYS = new Set(
  CHILE_HIGH_PURCHASING_CITY_NAMES.map((n) => normalizeChileCityKey(n)).filter(Boolean),
);

export function isChileHighPurchasingPowerCity(city?: string | null): boolean {
  const key = normalizeChileCityKey(city);
  if (!key) return false;
  if (CHILE_HIGH_PURCHASING_CITY_KEYS.has(key)) return true;
  // Substring guards for CF variants e.g. "Las Condes, Santiago"
  for (const known of CHILE_HIGH_PURCHASING_CITY_KEYS) {
    if (known.length >= 5 && (key.includes(known) || known.includes(key))) return true;
  }
  return false;
}

/** CL + ciudad high-purchasing → true (promo select DualCTA). */
export function shouldApplyChileHighPurchasingPromo(
  country?: string | null,
  city?: string | null,
): boolean {
  return String(country || "").trim().toUpperCase() === "CL" && isChileHighPurchasingPowerCity(city);
}

/** Fuente del promo VIP exterior para PostHog / Slack. */
export type VipPromoSource =
  | "mx_country"
  | "cl_high_purchasing"
  | "exterior_delfu80"
  | `exterior_${string}`
  | "admin_dual_cta"
  | "canonical"
  | "none";

/** DELFU80 constants for tracking (aliases of canonicalVipMembership). */
export const TRACK_DELFU80_PROMO_ID = SELECT_VIP_FIRST_MONTH_PROMO_ID;
export const TRACK_DELFU80_CODE = SELECT_VIP_PROMOTION_CODE_NAME;
export const TRACK_DELFU80_PERCENT = SELECT_VIP_FIRST_MONTH_DISCOUNT_PERCENT;
export const TRACK_VIP_PRICE_ID = CANONICAL_VIP_STRIPE_PRICE_ID;
export const TRACK_VIP_PRODUCT_ID = CANONICAL_VIP_STRIPE_PRODUCT_ID;

/**
 * Resolve vip_promo_source for exterior DELFU80 (all non-AR Stripe VIP).
 */
export function resolveVipPromoSource(input: {
  country?: string | null;
  city?: string | null;
  promoId?: string | null;
}): string {
  const cc = String(input.country || "").trim().toUpperCase();
  const city = String(input.city || "").trim();
  const promo = String(input.promoId || "").trim();
  // Engagement 80%: live SELECT_VIP (DELFU80INTL) + LEGACY id only for history/tracking.
  const isDelfu80 = !promo || isEngagementVipPromoId(promo);
  if (cc === "AR") return "none";
  if (cc === "MX" && isDelfu80) return "mx_country";
  if (shouldApplyChileHighPurchasingPromo(cc, city) && isDelfu80) {
    return "cl_high_purchasing";
  }
  if (cc && isDelfu80) return `exterior_${cc.toLowerCase()}`;
  if (promo.startsWith("promo_")) return "canonical";
  return "none";
}

/**
 * Full PostHog / Slack props for VIP promo — track ALL exterior DELFU80 paths.
 * Use on dual_cta_*, vip_checkout_started, dropoff, checkout_initiated.
 */
export function buildVipPromoTrackingProps(input: {
  country?: string | null;
  city?: string | null;
  promoId?: string | null;
  adminPromoId?: string | null;
  /** Override percent (default 80 for DELFU80). */
  discountPercent?: number | null;
}): Record<string, string | boolean | number> {
  const cc = String(input.country || "").trim().toUpperCase();
  const city = String(input.city || "").trim();
  const promoRaw = String(input.promoId || "").trim();
  const promo =
    promoRaw.startsWith("promo_")
      ? promoRaw
      : cc && cc !== "AR"
        ? TRACK_DELFU80_PROMO_ID
        : "";
  const clHigh = shouldApplyChileHighPurchasingPromo(cc, city);
  const isExterior = Boolean(cc && cc !== "AR");
  const isDelfu80 = isEngagementVipPromoId(promo);
  const source = resolveVipPromoSource({ country: cc, city, promoId: promo });
  const pct =
    input.discountPercent != null && input.discountPercent > 0
      ? Math.round(input.discountPercent)
      : isDelfu80
        ? TRACK_DELFU80_PERCENT
        : 0;

  const out: Record<string, string | boolean | number> = {
    vip_promo_source: source,
    chile_high_purchasing: clHigh,
    exterior_vip: isExterior,
    exterior_vip_delfu80: isExterior && isDelfu80,
    first_month_promo: isDelfu80 || pct > 0,
  };
  if (city) out.effective_city = city.slice(0, 80);
  if (cc) out.checkout_country = cc;
  if (promo.startsWith("promo_")) {
    out.vip_stripe_promotion_code_id = promo.slice(0, 80);
    out.promotion_code_id = promo.slice(0, 80);
  }
  if (isDelfu80) {
    out.vip_promo_code_name = TRACK_DELFU80_CODE;
    out.vip_first_month_discount_percent = pct || TRACK_DELFU80_PERCENT;
    out.vip_stripe_price_id = TRACK_VIP_PRICE_ID;
    out.vip_stripe_product_id = TRACK_VIP_PRODUCT_ID;
    out.vip_price_offer_line = `USD $${(
      (CANONICAL_VIP_PRICE_USD * (100 - (pct || TRACK_DELFU80_PERCENT))) /
      100
    ).toFixed(2)} primer mes (${pct || TRACK_DELFU80_PERCENT}% off)`;
  } else if (pct > 0) {
    out.vip_first_month_discount_percent = pct;
  }
  return out;
}
